/**
 * 🧪 Simulation Types — Simulação de Cenários
 */

export type ScenarioStatus = 'draft' | 'ready' | 'running' | 'completed' | 'failed';

export interface ScenarioInput {
  process_steps: number;
  avg_cycle_time_minutes: number;
  resources: number;
  batch_size: number;
  defect_rate_percent: number;
  demand_per_day: number;
}

export interface ScenarioOutput {
  lead_time_minutes: number;
  throughput_per_day: number;
  avg_queue_length: number;
  resource_utilization_percent: number;
  bottleneck_step?: number | null;
  takt_time_minutes: number;
}

export interface SimulationScenario {
  id: string;
  organization_id: string;
  name: string;
  description?: string | null;
  status: ScenarioStatus;
  base_process_id?: string | null;
  input: ScenarioInput;
  output?: ScenarioOutput | null;
  created_by?: string | null;
  project_id?: string | null;
  tags?: string[];
  created_at: string;
  updated_at: string;
}

export const SCENARIO_STATUS_LABELS: Record<ScenarioStatus, string> = {
  draft: 'Rascunho',
  ready: 'Pronto',
  running: 'Executando',
  completed: 'Concluído',
  failed: 'Falhou',
};

/** Calcula métricas de simulação com base nos inputs */
export function computeSimulation(input: ScenarioInput): ScenarioOutput {
  const { process_steps, avg_cycle_time_minutes, resources, batch_size, demand_per_day, defect_rate_percent } = input;
  const effectiveRate = 1 - defect_rate_percent / 100;
  const totalProcessingTime = process_steps * avg_cycle_time_minutes;
  const availableMinutesPerDay = 480; // 8h

  const taktTime = availableMinutesPerDay / demand_per_day;
  const leadTime = totalProcessingTime * batch_size / Math.max(resources, 1);
  const throughput = (resources * availableMinutesPerDay * effectiveRate) / totalProcessingTime;
  const utilization = Math.min(100, (demand_per_day / throughput) * 100);
  const avgQueue = Math.max(0, demand_per_day - throughput) * (totalProcessingTime / availableMinutesPerDay);

  return {
    lead_time_minutes: Math.round(leadTime * 100) / 100,
    throughput_per_day: Math.round(throughput * 100) / 100,
    avg_queue_length: Math.round(avgQueue * 100) / 100,
    resource_utilization_percent: Math.round(utilization * 100) / 100,
    takt_time_minutes: Math.round(taktTime * 100) / 100,
    bottleneck_step: null,
  };
}
